import { AnimationProps, Controller, ControllerUpdate, SpringConfig, SpringRef, SpringValues } from '@react-spring/web';
import React from 'react';
import { Boundary, Rect, ResizeDragState } from '../types';
import { Nullable } from 'src/types/nullable.type';
import { diffItems } from './diff-items';

export type ResizeBoundaryState = {
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
  padding: number;
  dragging: boolean;
};

export type ExtendedResizeBoundary = Boundary & {
  active: boolean;
  dragging: boolean;
};

const keyFn = (item: Boundary) => item.boundaryKey;

function pushBlockControllerUpdate(
  controller: Controller<ResizeBoundaryState>,
  props: ResizeBoundaryState,
  animationProps: AnimationProps<ResizeBoundaryState>
) {
  let started = 0;
  let skipped = 0;
  const updates: Partial<ResizeBoundaryState> = {};
  const springs = controller.springs;
  for (const key in props) {
    const rKey = key as keyof ResizeBoundaryState;
    if (rKey in springs && springs[rKey].goal !== props[rKey]) {
      // @ts-ignore
      updates[rKey] = props[rKey];
      started++;
    } else {
      skipped++;
    }
  }
  controller.start({
    ...updates,
    ...animationProps,
  });
  return { started, skipped };
}

const ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 400,
  friction: 40,
};

const PADDING = 8;

function toResizeBoundaryState(boundary: ExtendedResizeBoundary): ResizeBoundaryState {
  return {
    x: boundary.x - PADDING,
    y: boundary.y - PADDING,
    width: boundary.width + PADDING * 2,
    height: boundary.height + PADDING * 2,
    padding: PADDING,
    opacity: boundary.active || boundary.dragging ? 1 : 0,
    dragging: boundary.dragging,
  };
}

export function useAnimatedResizeBoundaries(
  boundaries: Boundary[],
  resizeDragState: Nullable<ResizeDragState>,
  hoverBlockLocation: Nullable<{ sectionIndex: number; rowIndex: number; colIndex: number }>,
  isDraggingSomethingOnEditor: boolean
) {
  const prevBoundariesRef = React.useRef<ExtendedResizeBoundary[]>([]);
  const controllersRef = React.useRef<Map<string, Controller<ResizeBoundaryState>>>(new Map());

  const extendedResizeBoundaries = React.useMemo(() => {
    return boundaries.map((re) => ({
      ...re,
      dragging: Boolean(
        resizeDragState?.boundary &&
          resizeDragState?.boundary?.insertionCol === re.insertionCol &&
          resizeDragState?.boundary?.insertionRow === re.insertionRow
      ),
      active: Boolean(
        !isDraggingSomethingOnEditor &&
          hoverBlockLocation &&
          re.orientation === 'horizontal' &&
          re.sectionIndex === hoverBlockLocation.sectionIndex &&
          re.insertionRow === hoverBlockLocation.rowIndex + 1
      ),
    }));
  }, [boundaries, resizeDragState, hoverBlockLocation, isDraggingSomethingOnEditor]);

  const springs = React.useMemo(() => {
    const prevBoundaries = prevBoundariesRef.current;

    // console.log('useAnimatedBoundaries', 'prevBoundaries', prevBoundaries);
    const { added, removed, updated } = diffItems(prevBoundaries, extendedResizeBoundaries, keyFn);

    // console.log(
    //   'useAnimatedBoundaries',
    //   'added',
    //   added.length,
    //   'removed',
    //   removed.length,
    //   'updated',
    //   updated.length
    // );

    prevBoundariesRef.current = extendedResizeBoundaries;

    for (const re of removed) {
      // console.log('useAnimatedBoundaries', 'removed', re);
      const id = keyFn(re);
      const controller = controllersRef.current.get(id);
      if (controller) {
        controller.each((spring) => spring.stop());
        controllersRef.current.delete(id);
      }
    }

    for (const re of updated) {
      // console.log('useAnimatedBoundaries', 'updated', re);
      const id = keyFn(re);
      const controller = controllersRef.current.get(id);
      if (controller) {
        const { started, skipped } = pushBlockControllerUpdate(controller, toResizeBoundaryState(re), {
          config: ANIMATION_CONFIG,
          immediate: true,
        });

        // console.log('useAnimatedBoundaries', 'updated', `started: ${started}, skipped: ${skipped}`);
      }
    }

    for (const re of added) {
      // console.log('useAnimatedBoundaries', 'added', re);
      const id = keyFn(re);
      const spring = SpringRef<ResizeBoundaryState>();
      const controller = new Controller({
        ref: spring,
        from: {
          ...toResizeBoundaryState(re),
          opacity: 0,
          dragging: false,
        },
      });
      controllersRef.current.set(id, controller);
      pushBlockControllerUpdate(controller, toResizeBoundaryState(re), {
        config: ANIMATION_CONFIG,
        immediate: true,
      });
    }

    return extendedResizeBoundaries.map((re) => {
      const id = keyFn(re);
      const controller = controllersRef.current.get(id);
      if (!controller) {
        throw new Error('Controller not found for render element: ' + id);
      }
      return controller.springs;
    });
  }, [extendedResizeBoundaries]);

  return { springs, extendedResizeBoundaries };
}
